import { Absence } from "@pentacode/core";

export const userFormatSettings = {
    timeZone: Temporal.Now.timeZoneId(),
    locale: "DE-de",
};

export function createTimeintervalFromNow(days: number) {
    const now = Temporal.Now.zonedDateTimeISO();
    return [
        now.subtract(Temporal.Duration.from({ days })).toPlainDateTime(),
        now.add(Temporal.Duration.from({ days })).toPlainDateTime(),
    ] as [to: Temporal.PlainDateTime, from: Temporal.PlainDateTime];
}

/**
 * Checks if a given Temporal.PlainDateTime is within the exclusive range (from, to).
 * The range is exclusive, meaning the endpoints are not included.
 *
 * @param {Temporal.PlainDateTime} time - The date-time to check.
 * @param {[from: Temporal.PlainDateTime, to: Temporal.PlainDateTime]} range - The [from, to] range to check against.
 * @returns {boolean} True if time is strictly between from and to, false otherwise.
 */
export function filterDateTimeByRangeExclusive(
    time: Temporal.PlainDateTime,
    range: [from: Temporal.PlainDateTime, to: Temporal.PlainDateTime]
) {
    return Temporal.PlainDateTime.compare(time, range[0]) + Temporal.PlainDateTime.compare(time, range[1]) === 0;
}

/**
 * Checks if a given Temporal.PlainDateTime is within the inclusive range [from, to].
 * The range is inclusive, meaning the endpoints are included.
 *
 * @param {Temporal.PlainDateTime} time - The date-time to check.
 * @param {[from: Temporal.PlainDateTime, to: Temporal.PlainDateTime]} range - The [from, to] range to check against.
 * @returns {boolean} True if time is between from and to, including the endpoints, false otherwise.
 */
export function filterDateTimeByRangeInclusive(
    time: Temporal.PlainDateTime,
    range: [from: Temporal.PlainDateTime, to: Temporal.PlainDateTime]
) {
    return Temporal.PlainDateTime.compare(time, range[0]) >= 0 && Temporal.PlainDateTime.compare(time, range[1]) <= 0;
}

export function getStartOfToday() {
    return Temporal.Now.plainDateTimeISO().with({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
        microsecond: 0,
        nanosecond: 0,
    });
}

/**
 * Returns true if the given time is after today (inclusive).
 * Note: "Today" IS included in the result.
 *
 * @param time - The Temporal.PlainDateTime to check.
 * @returns {boolean} True if the time is after today, false otherwise.
 */
export function filterDateTimeFromToday(time: Temporal.PlainDateTime) {
    return Temporal.PlainDateTime.compare(time, getStartOfToday()) >= 0;
}

/**
 * Returns true if the given time is before today (exclusive).
 * Note: "Today" is NOT included in the result.
 *
 * @param time - The Temporal.PlainDateTime to check.
 * @returns {boolean} True if the time is before today, false otherwise.
 */
export function filterDateTimeBeforeToday(time: Temporal.PlainDateTime) {
    return Temporal.PlainDateTime.compare(time, getStartOfToday()) < 0;
}

export function zonedToIso(date: Temporal.ZonedDateTime) {
    return date.toInstant().toString();
}

export function isoToPlainDateTime(iso: string) {
    return Temporal.PlainDateTime.from(iso);
}

export function isoToZoned(iso: string) {
    return Temporal.PlainDateTime.from(iso).toZonedDateTime(userFormatSettings.timeZone);
}

const dateFormatPreferences: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
};

const dateFormatTemplates: Record<string, Intl.DateTimeFormatOptions> = {
    birthday: {
        month: dateFormatPreferences.month,
        day: dateFormatPreferences.day,
    },
} as const;

export const FullDateFormatter = new Intl.DateTimeFormat(userFormatSettings.locale, dateFormatPreferences);
export const DayMonthFormatter = new Intl.DateTimeFormat(userFormatSettings.locale, dateFormatTemplates.birthday);
export const RelativeDayFormatter = new Intl.RelativeTimeFormat(userFormatSettings.locale, { numeric: "auto" });

export type TemporalEvent<T = Record<string, unknown>> = {
    date: Temporal.PlainDateTime;
    data: T;
};

export const formatDateRange = (from: string, to: string) => {
    const formatter = Intl.DateTimeFormat(userFormatSettings.locale, {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
    });
    return formatter.formatRange(new Date(from), new Date(to));
};
export const formatDateRangeParts = (from: string, to: string) => {
    const formatter = Intl.DateTimeFormat(userFormatSettings.locale, {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
    });
    return formatter.formatRangeToParts(new Date(from), new Date(to));
};

export const defaultRangeFormatter = (from: string, to: string) => {
    const formatter = Intl.DateTimeFormat(userFormatSettings.locale, {
        timeZone: "UTC",
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
    });

    return `${formatter.format(new Date(from))} – ${formatter.format(Temporal.PlainDateTime.from(to).subtract({ days: 1 }))}`;
};

export function getWeekdayCountsInRange(startDate: string, endDate: string): Map<number, number> {
    const weekdayCounts = new Map<number, number>();
    let current = Temporal.PlainDateTime.from(startDate);
    const end = Temporal.PlainDateTime.from(endDate);

    while (Temporal.PlainDateTime.compare(current, end) < 0) {
        const day = current.dayOfWeek % 7; // Temporal uses 1-7, convert to 0-6
        if (day >= 0 && day <= 6) {
            weekdayCounts.set(day, (weekdayCounts.get(day) || 0) + 1);
        }
        current = current.add({ days: 1 });
    }

    return weekdayCounts;
}

/**
 * Get the duration of an absence in days, only counting the days that are common work days
 * @param requestedDays - A map of weekday numbers to the number of days requested
 * @param commonWorkDays - An array of weekday numbers that are common work days
 * @returns The duration of the absence in days
 */
export function getAbsenceDuration(requestedDays: Map<number, number>, commonWorkDays: number[]): number {
    return Array.from(requestedDays.entries()).reduce((acc, [day, count]) => {
        if (commonWorkDays.includes(day)) {
            acc += count;
        }
        return acc;
    }, 0);
}

/**
 * Get the number of requested days for an absence (only counting common work days)
 * @param absence - The absence to get the requested days for
 * @param commonWorkDays - An array of weekday numbers that are common work days
 * @returns The number of requested days for the absence (only counting common work days)
 */
export function getAbsenceRequestedDays(absence: Absence, commonWorkDays: number[]): number {
    const requestedDays = getWeekdayCountsInRange(absence.start, absence.end);
    return getAbsenceDuration(requestedDays, commonWorkDays);
}
